import { FieldMergeFunction, FieldPolicy, Reference, isReference } from '@apollo/client';

// DD-3231: the order pages read orders from the normalized cache, and the list and details
// fragments select different sub-fields of the same un-normalized objects (created,
// archived, workFlow.currentStep, productionPlan, linked users). Apollo's default merge
// replaces such an object outright, so a list refetch would drop the sub-fields only the
// details page selected and force it back to the network. These merges keep them.

type Plain = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Plain =>
    !!value && typeof value === 'object' && !Array.isArray(value) && !isReference(value);

const identityOf = (value: unknown): string | null => {
    if (isReference(value)) return value.__ref;
    if (!isPlainObject(value)) return null;
    for (const key of ['_id', 'stepId', 'user']) {
        const candidate = value[key];
        if (candidate === undefined || candidate === null) continue;
        if (isReference(candidate)) return `${key}:${candidate.__ref}`;
        if (isPlainObject(candidate)) {
            const nested = identityOf(candidate);
            if (nested) return `${key}:${nested}`;
            continue;
        }
        return `${key}:${String(candidate)}`;
    }
    return null;
};

/**
 * Merges `incoming` into `existing`. Incoming always wins for the fields it carries; only
 * fields it does not select at all survive from `existing`. Arrays take incoming's items
 * and order, merging an item with the existing item of the same identity (_id, stepId or
 * user), so a list refetch never resurrects a removed row.
 */
export const deepMerge = (existing: unknown, incoming: unknown): unknown => {
    if (Array.isArray(incoming)) {
        if (!Array.isArray(existing)) return incoming;
        const existingByIdentity = new Map<string, unknown>();
        existing.forEach((item) => {
            const identity = identityOf(item);
            if (identity) existingByIdentity.set(identity, item);
        });
        return incoming.map((item) => {
            const identity = identityOf(item);
            return identity && existingByIdentity.has(identity)
                ? deepMerge(existingByIdentity.get(identity), item)
                : item;
        });
    }
    if (isPlainObject(incoming) && isPlainObject(existing)) {
        const merged: Plain = { ...existing };
        Object.keys(incoming).forEach((key) => {
            merged[key] = deepMerge(existing[key], incoming[key]);
        });
        return merged;
    }
    return incoming === undefined ? existing : incoming;
};

export const deepMergeField: { merge: FieldMergeFunction } = {
    merge: (existing: unknown, incoming: unknown) => deepMerge(existing, incoming) as Reference,
};

/**
 * A shallow merge of an optional, un-normalized object (an order's patient or delivery
 * details) that keeps an explicit null. Turning null into `{}` left a patient-less order
 * (DD-3494) with a patient missing every field, so each list query reading it saw
 * incomplete data and refetched from offset 0 — wiping the loaded pages and closing the
 * row menu.
 */
export const shallowMergeKeepingNull: { merge: FieldMergeFunction } = {
    merge: (existing: unknown, incoming: unknown) => {
        if (incoming === null) return null;
        if (incoming === undefined) return (existing ?? null) as Reference | null;
        return { ...(isPlainObject(existing) ? existing : {}), ...(incoming as Plain) } as unknown as Reference;
    },
};

/**
 * Paged order lists: offset 0 replaces the list, a later offset appends. Unlike
 * offsetLimitPagination, a re-read of the loaded window from offset 0 (DD-3231) can shrink
 * the list, so an order that left it does not linger as a duplicate of the last row.
 */
export const replaceFromOffsetZero = (keyArgs: string[]): FieldPolicy<unknown[]> => ({
    keyArgs,
    merge(existing = [], incoming, { args }) {
        if (!args?.offset) {
            return incoming;
        }
        return [...existing, ...incoming];
    },
});
